import { create } from 'zustand';

// Tab-scoped store for credentials the user just typed at signup, used
// to auto-login them after email verification so they skip the
// /login bounce.
//
// Persistence is sessionStorage (not localStorage): the password
// lives only for the lifetime of the tab, gets cleared the moment
// the auto-login succeeds, the user cancels, or the tab closes.
// Same security profile as keeping them in JS memory — the password
// is already in browser memory the moment the user types it — but
// survives Vite HMR / route navigation / accidental refresh, which
// the pure in-memory store didn't, leaving the user stranded back
// on /login with no creds to auto-login with.
//
// Lives in a global zustand slice instead of component-local state
// because SignUpPage stashes the creds, then navigates to a different
// route (VerifyEmailPage) that mounts its own useAuthActions() — the
// creds have to survive the cross-route navigation.
interface PendingSignupCreds {
  email: string;
  password: string;
}

interface PendingSignupState {
  creds: PendingSignupCreds | null;
  setCreds: (creds: PendingSignupCreds) => void;
  clearCreds: () => void;
}

const STORAGE_KEY = 'biasafe_pending_signup_creds';

function readFromSession(): PendingSignupCreds | null {
  if (typeof window === 'undefined') return null;
  try {
    const raw = window.sessionStorage.getItem(STORAGE_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw);
    if (
      parsed &&
      typeof parsed.email === 'string' &&
      typeof parsed.password === 'string'
    ) {
      return parsed;
    }
    return null;
  } catch {
    return null;
  }
}

function writeToSession(creds: PendingSignupCreds | null): void {
  if (typeof window === 'undefined') return;
  try {
    if (creds) {
      window.sessionStorage.setItem(STORAGE_KEY, JSON.stringify(creds));
    } else {
      window.sessionStorage.removeItem(STORAGE_KEY);
    }
  } catch {
    /* sessionStorage can throw in private mode or quota — swallow */
  }
}

export const usePendingSignupStore = create<PendingSignupState>((set) => ({
  // Hydrate from sessionStorage on first read so a Vite HMR reload or
  // accidental refresh between signup and verify still has the creds.
  creds: readFromSession(),
  setCreds: (creds) => {
    writeToSession(creds);
    set({ creds });
  },
  clearCreds: () => {
    writeToSession(null);
    set({ creds: null });
  },
}));
